import { useEffect, useRef } from "react";
import { FOLDER_HEADERS_PREFETCH_DEFER_MS } from "@/lib/constants/inbox";
import { MAIL_AUTO_FOLDER_PREFETCH_ENABLED } from "@/lib/constants/mailStorage";
import { countFoldersNeedingPrefetch } from "@/lib/mail/folderHeadersPrefetch";
import { launchFolderHeadersPrefetch } from "@/lib/mail/folderHeadersPrefetchProgress";
import type { MailFolder } from "@/lib/types/mail";

/**
 * Après le boot : prépare silencieusement les en-têtes des dossiers restants (DD02 d’abord).
 * Une fois marqués (bootstrap store + DD02), les refresh suivants ne refont pas IMAP.
 */
export function useAutoFolderHeadersPrefetch(
  accountId: number | undefined,
  folders: MailFolder[],
  options?: { enabled?: boolean }
): void {
  const enabled = options?.enabled !== false && MAIL_AUTO_FOLDER_PREFETCH_ENABLED;
  const foldersRef = useRef(folders);
  foldersRef.current = folders;
  const startedRef = useRef(new Set<number>());

  useEffect(() => {
    if (!enabled || accountId == null || folders.length === 0) return;
    if (startedRef.current.has(accountId)) return;

    const { ready, total } = countFoldersNeedingPrefetch(accountId, folders);
    if (ready >= total) {
      startedRef.current.add(accountId);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      startedRef.current.add(accountId);
      void launchFolderHeadersPrefetch(accountId, foldersRef.current);
    }, FOLDER_HEADERS_PREFETCH_DEFER_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [accountId, enabled, folders.length]);
}
